import type { Schemas } from '@kanos/api-client';
import { paragraphsOf } from '@kanos/domain';
import { Button, ModalDialog, StatusBadge } from '@kanos/ui';
import { api, failure, formatDate, QueryContent } from '@kanos/web-core';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { keys } from '../firm/queries';

type AnnouncementFeed = Schemas['AnnouncementFeed'];

const feedKey = (tenantId: string) => [...keys.all(tenantId), 'announcements'] as const;

/** What is new in Kanos - asked again now and then, so news reach an open app. */
const announcementsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: feedKey(tenantId),
    queryFn: async (): Promise<AnnouncementFeed> => {
      const { data, error, response } = await api.GET('/api/v1/help/announcements');
      if (!data) throw failure('listAnnouncements', error, response.status);
      return data;
    },
    refetchInterval: 10 * 60_000,
    staleTime: 60_000,
  });

/** How many the person has not read - for the dot on the help button. */
export function useUnreadAnnouncements(tenantId: string): number {
  const feed = useQuery(announcementsQuery(tenantId));
  return feed.data?.items.filter((i) => i.unread).length ?? 0;
}

/** The feed is asked again only once the dialog closes: until then, what was new keeps its mark. */
function useMarkRead() {
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.PUT('/api/v1/help/announcements/read');
      if (!response.ok) throw failure('markAnnouncementsRead', error, response.status);
    },
  });
}

/**
 * What is new in Kanos, newest first: opened, it counts as read - the new ones keep their mark
 * while the dialog is open, so the person sees what came since last time.
 */
export function AnnouncementsDialog({
  tenantId,
  isOpen,
  onClose,
}: {
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const feed = useQuery(announcementsQuery(tenantId));
  const { mutate: markRead, isSuccess: marked } = useMarkRead();
  const hasUnread = feed.data?.items.some((i) => i.unread) ?? false;
  useEffect(() => {
    if (isOpen && hasUnread) markRead();
  }, [isOpen, hasUnread, markRead]);

  return (
    <ModalDialog
      title="Neuigkeiten"
      description="Was sich in Kanos ändert – vom Kanos-Team."
      size="lg"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (open) return;
        if (marked) void queryClient.invalidateQueries({ queryKey: feedKey(tenantId) });
        onClose();
      }}
      footer={(close) => (
        <Button variant="secondary" onPress={close}>
          Schließen
        </Button>
      )}
    >
      {() => (
        <QueryContent query={feed}>
          {({ items }) =>
            items.length === 0 ? (
              <p className="text-sm text-muted">Noch keine Neuigkeiten.</p>
            ) : (
              <ol className="grid gap-6">
                {items.map((item) => (
                  <li key={item.id} className="grid gap-2">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h3 className="font-semibold">{item.title}</h3>
                      {item.unread && <StatusBadge tone="info">Neu</StatusBadge>}
                    </div>
                    <p className="text-sm text-muted">{formatDate(item.publishedAt)}</p>
                    {paragraphsOf(item.body).map((paragraph) => (
                      <p key={paragraph} className="max-w-prose text-[0.9375rem] leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </li>
                ))}
              </ol>
            )
          }
        </QueryContent>
      )}
    </ModalDialog>
  );
}
